import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { usePingStore } from "@/stores/ping-store";
import { PhoneLogin } from "./PhoneLogin";
import { Card } from "@/components/ui/card";
import { useStaffUpcomingAppointments } from "@/hooks/useStaffAppointments";
import { Calendar, Users, UserCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { AppointmentsWidget } from "@/components/widgets/AppointmentsWidget";

export const HomeDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { patientUnreadCount, teamsUnreadCount } = usePingStore();

  const { data: upcomingData } = useStaffUpcomingAppointments(1);

  const handleNavigateToPings = (tab: "patient" | "teams") => {
    if (tab === "teams") {
      navigate("/dashboard/internal-chat");
    } else {
      navigate("/dashboard/chat");
    }
  };

  if (!isAuthenticated) {
    return <PhoneLogin />;
  }

  return (
    <div className="flex h-full overflow-y-auto p-2 sm:p-3">
      <div className="flex min-h-0 flex-1 items-start gap-2 sm:gap-3">
        {/* Left Column - Appointments Widget */}
        <div className="flex-1 min-w-0">
          <AppointmentsWidget />
        </div>

        {/* Right Column - Quick Actions */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col border border-border rounded-xl overflow-hidden bg-card">
            <div className="px-6 pt-5 pb-4 border-b border-border shrink-0">
              <h2 className="z-h2 text-foreground">Quick Actions</h2>
              <p className="z-secondary text-muted-foreground mt-0.5">
                Jump to your priorities
              </p>
            </div>

            <div className="p-6">
              <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {/* Appointments Card */}
                <Card
                  className="overflow-hidden cursor-pointer transition-shadow duration-200 hover:shadow-md border-border"
                  onClick={() => {}}
                >
                  <div className="p-4 flex items-start justify-between">
                    <div>
                      <p className="z-secondary text-muted-foreground">
                        Appointments
                      </p>
                      <p className="z-h1 tabular-nums text-foreground mt-1">
                        {upcomingData?.count || 0}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </div>
                </Card>

                {/* Team Pings Card */}
                <Card
                  className={cn(
                    "overflow-hidden cursor-pointer transition-shadow duration-200 hover:shadow-md",
                    teamsUnreadCount > 0 ? "border-primary/40" : "border-border"
                  )}
                  onClick={() => handleNavigateToPings("teams")}
                >
                  <div className="p-4 flex items-start justify-between">
                    <div>
                      <p className="z-secondary text-muted-foreground">
                        Team Pings
                      </p>
                      <p className="z-h1 tabular-nums text-foreground mt-1">
                        {teamsUnreadCount > 99 ? "99+" : teamsUnreadCount}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0 relative">
                      <Users className="w-5 h-5 text-muted-foreground" />
                      {teamsUnreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground z-caption flex items-center justify-center font-medium">
                          {teamsUnreadCount > 99 ? "99+" : teamsUnreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Patient Pings Card */}
                <Card
                  className={cn(
                    "overflow-hidden cursor-pointer transition-shadow duration-200 hover:shadow-md",
                    patientUnreadCount > 0
                      ? "border-primary/40"
                      : "border-border"
                  )}
                  onClick={() => handleNavigateToPings("patient")}
                >
                  <div className="p-4 flex items-start justify-between">
                    <div>
                      <p className="z-secondary text-muted-foreground">
                        Patient Pings
                      </p>
                      <p className="z-h1 tabular-nums text-foreground mt-1">
                        {patientUnreadCount > 99 ? "99+" : patientUnreadCount}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0 relative">
                      <UserCircle className="w-5 h-5 text-muted-foreground" />
                      {patientUnreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground z-caption flex items-center justify-center font-medium">
                          {patientUnreadCount > 99 ? "99+" : patientUnreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
